<style>
.table tr.first-agent {
  border-top-width: 2px;
}
.table tr.same-agent {
  border-top-width: 0px;
}
.table tr.summary-row {
  background-color: #f3f4f6;
  border-top: 1px solid #d1d5db;
  font-size: 0.875rem;
  height: 2.5rem;
}
.dark .table tr.summary-row {
  @apply bg-tertiary-dark;
  @apply text-white;
}
</style>

<script>
import { Query, useQueryClient } from "@sveltestack/svelte-query"
import API from "@local/utils/api"
import { link } from "svelte-routing"
import Pager from "@local/components/Pager.svelte"
import EmptyMessage from "@local/components/EmptyMessage.svelte"
import TextInput from "@local/components/form/TextInput.svelte"
import Icon from "@local/components/images/Icon.svelte"
import CreateCallOutcomeModal from "@local/components/CreateCallOutcomeModal.svelte"
import MultiSelect from "@local/components/MultiSelect.svelte"
import {
  setDataToLocalStorage,
  getDataFromLocalStorage,
} from "@local/utils/localStorage"
import BackToTop from "@local/components/BackToTop.svelte"
import * as XLSX from "xlsx"

const queryClient = useQueryClient()
const localStorageKey = "whoToCallUsers"
const reasonOptions = [
  "Expiring 60 Days",
  "Expiring 30 Days",
  "Nearly Expired",
  "Lapsed 30 Days",
  "Lapsed 60 Days",
  "Lapsed 120 Days",
  "Lapsed 180 Days",
  "First Follow-up",
  "10-day Follow-up",
  "30-day Follow-up",
]
const subdomainsOptions = [
  "starseed",
  "mymedi",
  "pearson",
  "aurora",
  "tilray",
  "seniorcareroc",
  "massageaddict",
  "mendo",
  "herbaldispatch",
  "tantus",
  "cannafarms",
  "cccc",
  "ccct",
  "alternapharma",
  "aqualitas",
  "blackrose",
  "floweroflife",
  "vitalicann",
  "zyus",
]
const tagOptions = [
  "Platinum",
  "Gold",
  "Silver",
  "Bronze",
  "Limited Purchase",
  "No Purchase",
  "No Record",
]
const langOptions = ["en", "fr"]
const veteranOptions = ["Yes", "No"]

let perFilter = 20
let storedFilters = getDataFromLocalStorage(localStorageKey)
let query = storedFilters?.query || ""
let queryVal = storedFilters?.query || ""
let reasonsVal = []
let subdomainsVal = []
let tagsVal = []
let langsVal = []
let veteransVal = []
let callerTargetVal
let page = storedFilters?.page || 0
let edit = ""
let wfId
let filterGroups = []

async function fetchWhoToCallUsers(
  page,
  query,
  reasons,
  subdomains,
  tags,
  langs,
  veterans
) {
  const searchParams = {
    page,
    perFilter,
    query,
    reasons,
    subdomains,
    tags,
    langs,
    veterans,
  }
  const response = await API.get(`admin/who-to-call/users`, { searchParams })
  const rows = await response.json()
  const hasNextPage = rows.length > 0
  const count = parseInt(response.headers.get("X-Total-Count"))

  setDataToLocalStorage(localStorageKey, {
    page,
    query,
  })
  return { rows, hasNextPage, query, count }
}

async function handleSave(id) {
  await API.patch(`admin/who-to-call/users/${id}`, {
    json: {
      filterGroups,
    },
  })
    .then(() => {
      queryClient.invalidateQueries()
    })
    .catch((err) => alert(`FAILED REMOVING RECORD WITH ERROR ${err}`))

  edit = ""
  filterGroups = []
}

async function handleToggleEdit(row) {
  if (edit === row.id) {
    edit = ""
    filterGroups = []
    callerTargetVal = null
  } else {
    edit = row.id
    if (
      Array.isArray(row.callerFilterGroups) &&
      row.callerFilterGroups.length > 0
    ) {
      filterGroups = row.callerFilterGroups.map((g) => ({ ...g }))
    } else {
      filterGroups = [
        {
          subdomains: row.callerSubdomains || [],
          reasons: row.callerReasons || [],
          tags: row.callerTags || [],
          langs: row.callerLangs || [],
          veterans: row.callerVeterans || [],
          target: row.callerTarget || row.count || null,
        },
      ]
    }
    callerTargetVal = row.callerTarget || row.count
  }
}

function addFilterGroup() {
  filterGroups = [
    ...filterGroups,
    {
      subdomains: [],
      reasons: [],
      tags: [],
      langs: [],
      veterans: [],
      target: null,
    },
  ]
}

function removeFilterGroup(idx) {
  // If there's only one filter group, clear its values instead of removing it
  if (filterGroups.length === 1) {
    filterGroups = [
      {
        subdomains: [],
        reasons: [],
        tags: [],
        langs: [],
        veterans: [],
      },
    ]
  } else {
    // Create a new array to ensure Svelte reactivity
    filterGroups = filterGroups.filter((_, i) => i !== idx)
  }
}

function buildFileName() {
  const now = new Date()
  const month = (now.getMonth() + 1).toString().padStart(2, "0")
  const year = now.getFullYear()
  return `who-to-call-agents-${year}-${month}`
}

function downloadXLSX(rows) {
  if (!rows || rows.length === 0) return
  // Cabeçalhos
  const header = [
    "User",
    "Email",
    "Subdomains",
    "Call Tags",
    "Purchase Tags",
    "Language",
    "Veterans?",
    "Done",
    "Target",
  ]
  // Dados
  const data = rows.map((row) => [
    row.name,
    row.email,
    row.callerSubdomains ? row.callerSubdomains.join(", ") : "",
    row.callerReasons ? row.callerReasons.join(", ") : "",
    row.callerTags ? row.callerTags.join(", ") : "",
    row.callerLangs ? row.callerLangs.join(", ") : "",
    row.callerVeterans ? row.callerVeterans.join(", ") : "",
    row.count,
    row.callerTarget || "-",
  ])
  const worksheet = XLSX.utils.aoa_to_sheet([header, ...data])
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, "WTC-Agents")
  const fileName = buildFileName() + ".xlsx"
  XLSX.writeFile(workbook, fileName)
}

$: queryOptions = {
  queryKey: ["whoToCallUsers", page, query],
  queryFn: () => fetchWhoToCallUsers(page, query),
  keepPreviousData: true,
  refetchOnWindowFocus: false,
}
</script>

<Query options="{queryOptions}">
  <div class="p-5" slot="query" let:queryResult>
    {#if queryResult.isLoading}
      <div></div>
    {:else if queryResult.error}
      <EmptyMessage icon="error">{queryResult.error}</EmptyMessage>
    {:else}
      <div class="flex flex-row items-center justify-between mb-6">
        <h2 class="h2">
          {#if queryResult.data.query}
            CS Agents Search: "{queryResult.data.query}"
          {:else}CS Agents{/if}
        </h2>
      </div>
      <div class="flex w-full items-center gap-2 mb-2">
        <Pager
          bind:page="{page}"
          hasNextPage="{queryResult.data.hasNextPage}"
          isPreviousData="{queryResult.isPreviousData}"
        />
        {#if queryResult.data.rows.length > 0}
          <button
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 btn btn-outline"
            type="button"
            on:click="{() => downloadXLSX(queryResult.data.rows)}"
            title="Download XLSX"
          >
            <Icon size="3xl">download</Icon>
          </button>
        {/if}
      </div>
      {#if queryResult.data.rows.length > 0}
        <table class="table">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Filters</th>
              <th class="text-white">Call</th>
              <th class="text-white">Purchase</th>
              <th class="text-white">Lang</th>
              <th class="text-white">Vets</th>
              <th class="text-white">Curr/Target</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {#each queryResult.data.rows as row}
              {#if edit === row.id}
                {#each filterGroups.length > 0 ? filterGroups : [{}] as group, idx}
                  <tr class="bg-gray-200">
                    {#if idx === 0}
                      <td><b>{row.name}</b><br /><i>{row.email}</i></td>
                    {:else}
                      <td></td>
                    {/if}
                    <td>
                      <MultiSelect
                        className="w-full md:ml-2 mt-2 md:mt-0"
                        bind:value="{group.subdomains}"
                        placeholder="Subdomains"
                      >
                        {#each subdomainsOptions as tag}
                          <option value="{tag}">{tag}</option>
                        {/each}
                      </MultiSelect>
                    </td>
                    <td>
                      <MultiSelect
                        className="w-full md:ml-2 mt-2 md:mt-0"
                        bind:value="{group.reasons}"
                        placeholder="Call Tags"
                      >
                        {#each reasonOptions as tag}
                          <option value="{tag}">{tag}</option>
                        {/each}
                      </MultiSelect>
                    </td>
                    <td>
                      <MultiSelect
                        className="w-full md:ml-2 mt-2 md:mt-0"
                        bind:value="{group.tags}"
                        placeholder="Purchase Tags"
                      >
                        {#each tagOptions as tag}
                          <option value="{tag}">{tag}</option>
                        {/each}
                      </MultiSelect>
                    </td>
                    <td>
                      <MultiSelect
                        className="w-full md:ml-2 mt-2 md:mt-0"
                        bind:value="{group.langs}"
                        placeholder="Lang"
                      >
                        {#each langOptions as lang}
                          <option value="{lang}">{lang}</option>
                        {/each}
                      </MultiSelect>
                    </td>
                    <td>
                      <MultiSelect
                        className="w-full md:ml-2 mt-2 md:mt-0"
                        bind:value="{group.veterans}"
                        placeholder="Vets"
                      >
                        {#each veteranOptions as vet}
                          <option value="{vet}">{vet}</option>
                        {/each}
                      </MultiSelect>
                    </td>
                    <td class="w-16">
                      {#if idx === filterGroups.length - 1}
                        <button
                          type="button"
                          class="focus:outline-none"
                          on:click="{addFilterGroup}"
                          title="Add group"
                        >
                          <Icon>add</Icon>
                        </button>
                      {/if}
                      {#if idx === 0}
                        <button
                          type="button"
                          class="focus:outline-none"
                          on:click="{() => handleSave(row.id)}"
                          title="Save"
                        >
                          <Icon>save</Icon>
                        </button>
                      {/if}
                    </td>
                    <td>
                      {#if idx === filterGroups.length - 1}
                        <button
                          type="button"
                          class="focus:outline-none"
                          on:click="{() => removeFilterGroup(idx)}"
                          title="Remove group"
                        >
                          <Icon>delete</Icon>
                        </button>
                      {/if}
                    </td>
                    <td></td>
                  </tr>
                {/each}
              {:else if row.callerFilterGroups && row.callerFilterGroups.length > 0}
                {#each row.callerFilterGroups as group, idx}
                  <tr
                    class:first-agent="{idx === 0}"
                    class:same-agent="{idx !== 0}"
                  >
                    {#if idx === 0}
                      <td>
                        <b>{row.name}</b><br />
                        <i>{row.count} / {row.countTarget} total</i><br />
                        {row.countTarget > 0
                          ? ((row.count / row.countTarget) * 100).toFixed(2)
                          : 0}% completed
                      </td>
                    {:else}
                      <td></td>
                    {/if}
                    <td>
                      {group.subdomains ? group.subdomains.join(", ") : []}
                    </td>
                    <td>
                      {group.reasons ? group.reasons.join(", ") : []}
                    </td>
                    <td>
                      {group.tags ? group.tags.join(", ") : []}
                    </td>
                    <td>
                      {group.langs ? group.langs.join(", ") : []}
                    </td>
                    <td>
                      {group.veterans ? group.veterans.join(", ") : []}
                    </td>
                    <td>
                      {group.count} / {group.countTarget}
                    </td>
                    <td>
                      {#if idx === 0}
                        <button
                          type="button"
                          class="focus:outline-none"
                          on:click="{() => handleToggleEdit(row)}"
                          title="Edit"
                        >
                          <Icon>edit</Icon>
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              {:else}
                <tr class="first-agent">
                  <td>
                    <b>{row.name}</b><br />
                  </td>
                  <td>all</td>
                  <td></td>
                  <td></td>
                  <td> </td>
                  <td> </td>
                  <td> </td>
                  <td>
                    <button
                      type="button"
                      class="focus:outline-none"
                      on:click="{() => handleToggleEdit(row)}"
                      title="Edit"
                    >
                      <Icon>edit</Icon>
                    </button>
                  </td>
                </tr>
              {/if}
            {/each}
            <tr class="summary-row">
              <td colspan="6" class="text-right font-semibold">Total:</td>
              <td class="font-semibold">
                {queryResult.data.rows.reduce((total, row) => {
                  if (
                    row.callerFilterGroups &&
                    row.callerFilterGroups.length > 0
                  ) {
                    return (
                      total +
                      row.callerFilterGroups.reduce((groupTotal, group) => {
                        return groupTotal + (group.count || 0)
                      }, 0)
                    )
                  }
                  return total
                }, 0)} / {queryResult.data.rows.reduce((total, row) => {
                  if (
                    row.callerFilterGroups &&
                    row.callerFilterGroups.length > 0
                  ) {
                    return (
                      total +
                      row.callerFilterGroups.reduce((groupTotal, group) => {
                        return groupTotal + (group.countTarget || 0)
                      }, 0)
                    )
                  }
                  return total
                }, 0)}
              </td>
              <td colspan="2"></td>
            </tr>
          </tbody>
        </table>
      {:else}
        <EmptyMessage icon="check">No users at the moment</EmptyMessage>
      {/if}
    {/if}
  </div>
</Query>

<BackToTop />
